import { useEffect, useState, useSyncExternalStore } from "react";
import { useFormContext } from "react-hook-form";
import currency from "currency.js";

import { Quote } from "@/shared/types/Quote.interface";
import { CoverageUtils } from "@/shared/utils/CoverageUtils";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { useFormParentContext } from "@/shared/contexts/FormParent";

export type BillingFrequencyType = "monthly" | "yearly";

const MONTHLY_FEE_CACHE_PREFIX = "spot:monthly-transaction-fee:";

/** sessionStorage never notifies, so the store below only ever re-reads on render. */
const subscribeToNothing = () => () => {};

/**
 * The last monthly transaction fee seen for a given quote, remembered for the rest of the browser tab.
 *
 * An annual quote carries no `transactionFee` field at all, so a Billing step that MOUNTS on one — a
 * refresh after choosing annual, or a resume/deep link, since formController restores `lastStepID` —
 * has no fee to work from. Everything the annual-savings framing is made of hangs off that fee (the
 * green container, the struck price, the "You're saving $X" line), so all of it drops out until the
 * customer toggles back to monthly and the reprice reveals the fee again.
 *
 * Scoped to the quote id so one quote can never inherit another's fee, and to the session so it cannot
 * outlive the tab. Storage access throws outright in some privacy modes, hence the try/catch.
 */
const readCachedMonthlyFee = (quoteId?: string): number | null => {
    if (!quoteId || typeof window === "undefined") return null;
    try {
        const cached = window.sessionStorage.getItem(`${MONTHLY_FEE_CACHE_PREFIX}${quoteId}`);
        const parsed = cached === null ? NaN : Number(cached);
        return Number.isFinite(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

const cacheMonthlyFee = (quoteId: string | undefined, fee: number) => {
    if (!quoteId || typeof window === "undefined") return;
    try {
        window.sessionStorage.setItem(`${MONTHLY_FEE_CACHE_PREFIX}${quoteId}`, String(fee));
    } catch {
        // Storage unavailable (private mode, quota). The fee still works for this mount.
    }
};

/**
 * Shared billing-frequency logic for the Billing/Checkout step.
 *
 * Owns the selected-frequency state, the monthly transaction-fee display flag,
 * the derived annual-savings amount, and the `handleFrequencyChange` mutation.
 * Used by both PTZ-US and PTZ-CA so the two forms stay in sync.
 *
 * Reads `updateQuote`, `isQuoteUpdating` and the calculated `billingFrequency`
 * from AppLayer, the frequency field from the step form context, and the full
 * quote from the FormParent context.
 *
 * NOTE: deliberately has no `priority-code-deprecated` retry, unlike the other quote-writing call
 * sites (CoverageEditor, PetModal, UserModal, useQuote, ptz-us formController/formConfig). It would be
 * unreachable here: ptz-ca never raises that error, and on ptz-us the pcode is already resolved by the
 * Pets-step `retryOnPcodeError` (it fails deterministically — "priority code is for existing customer
 * only" is customer-scoped, not time-based — so it can't newly appear at the Billing step). A failed
 * update just rolls back instead, which also avoids silently deleting the customer's promo code and
 * changing their price mid-toggle.
 */
export function useBillingFrequency(quote: Quote) {
    const { appState, updateAppState } = useAppLayerContext();
    const { isQuoteUpdating, updateQuote, billingFrequency: calculatedFrequency } = appState;

    const { setValue } = useFormContext();
    const { getValues } = useFormParentContext<Quote>();

    const currentFrequency = (quote?.billingInfo?.frequency ?? "monthly") as BillingFrequencyType;
    const transactionFee = quote.transactionFee?.value ?? 0;

    // Store the monthly fee on mount, then keep it in sync while on monthly billing.
    const [storedMonthlyFee, setStoredMonthlyFee] = useState(() => (currentFrequency === "monthly" ? transactionFee : 0));

    // The latch has to survive the switch to annual (where `transactionFee` is no longer the monthly
    // fee), so it stays state; it is re-synced during render while on monthly rather than in an
    // effect. The `!==` guard is what stops the update from looping.
    if (currentFrequency === "monthly" && storedMonthlyFee !== transactionFee) {
        setStoredMonthlyFee(transactionFee);
    }

    // Read through `useSyncExternalStore` rather than in the `useState` initialiser above on purpose:
    // `sessionStorage` does not exist while the component is server-rendered, so seeding state from it
    // would make the server and client markup disagree. The server snapshot is `null`, so SSR and the
    // hydrating render agree on "no cache" and the client value lands on the next render.
    const cachedMonthlyFee = useSyncExternalStore(
        subscribeToNothing,
        () => readCachedMonthlyFee(quote?.id),
        () => null
    );

    // Mounted on annual, the quote itself can't tell us the fee, so fall back to the last one this tab
    // saw for this quote. A fee observed live on this mount always wins over the cache.
    const monthlyFee = storedMonthlyFee > 0 ? storedMonthlyFee : (cachedMonthlyFee ?? 0);

    // Remember the live monthly fee for the rest of the tab (see `readCachedMonthlyFee`). Only a real fee
    // is written, so a not-yet-priced monthly quote can't overwrite a good value with 0.
    useEffect(() => {
        if (currentFrequency === "monthly" && transactionFee > 0) cacheMonthlyFee(quote?.id, transactionFee);
    }, [currentFrequency, transactionFee, quote?.id]);

    const showTransactionFee = monthlyFee > 0;
    // Annual billing waives the monthly transaction fee, so the yearly saving is
    // the monthly fee × 12. Derived from the real fee rather than hardcoded so
    // it stays correct across markets (US, CA, …) whatever the API returns.
    const annualSavings = monthlyFee * 12;

    // ENG-18612 prices BOTH schedules at once, so neither total can be read straight off the quote:
    // `finalPrice` is already scaled to whichever frequency the quote is currently priced at, and
    // `transactionFee` is absent while on annual. Normalise to the annual premium, then derive the rest.
    //
    // `currentFrequency` — not `frequencyToDisplay` — is what these key off: it is the frequency the
    // STORED quote was priced at, which is what `finalPrice` reflects. Keying off the optimistic display
    // frequency would divide/multiply by 12 one render before the repriced quote lands.
    const policiesSubtotal = CoverageUtils.calculatePolicyTotal(quote?.policies);
    // Premium only — annual billing waives the fee, so nothing is added here.
    const annualTotal = currentFrequency === "yearly" ? policiesSubtotal : currency(policiesSubtotal).multiply(12).value;
    const monthlyTotal = currency(annualTotal).divide(12).add(monthlyFee).value;
    // The annual card's struck-through price: a year of monthly billing, i.e. the annual premium plus
    // the 12 monthly fees annual billing waives.
    const annualTotalIfBilledMonthly = currency(annualTotal).add(annualSavings).value;

    const frequencyToDisplay = (calculatedFrequency ?? currentFrequency) as BillingFrequencyType;
    const [selectedBillingFrequency, setSelectedBillingFrequency] = useState<BillingFrequencyType>(frequencyToDisplay);

    /**
     * Put the toggle, the form field and the app state back on the frequency the STORED quote has.
     *
     * Every failed-persist path must land here. The frequency the customer is actually billed is read
     * from the stored quote at checkout (ptz-ca sends it to Orca at finalize; it is never carried by the
     * payment session), so a UI left showing a frequency the quote never accepted bills them the other
     * one. The form field is reset too — the optimistic `setValue` below must not survive a failed
     * persist, or the step submits a frequency the quote rejected.
     */
    const rollbackFrequency = () => {
        setValue("billingInfo.frequency", currentFrequency, { shouldTouch: true, shouldDirty: true });
        setSelectedBillingFrequency(currentFrequency);
        updateAppState({
            billingFrequency: undefined,
            isAnnualBilling: currentFrequency === "yearly"
        });
    };

    const handleFrequencyChange = async (newValue: BillingFrequencyType) => {
        // Bail before touching the UI when there's nothing to persist with — moving the toggle without
        // a mutation would leave the same UI/quote mismatch a failed update does.
        if (isQuoteUpdating || !updateQuote) return;

        // Update the form state for the frequency value
        setValue("billingInfo.frequency", newValue, { shouldTouch: true, shouldDirty: true });
        setSelectedBillingFrequency(newValue);

        const onUpdateQuote = async () => {
            const formValues = getValues();
            try {
                return await updateQuote?.mutateAsync({
                    quote: {
                        ...formValues,
                        billingInfo: { frequency: newValue }
                    }
                });
            } catch (error) {
                console.error("Error updating quote frequency:", error);
                rollbackFrequency();
                return undefined;
            }
        };

        // Update the quote asynchronously
        const updatedQuote = await onUpdateQuote();

        // On a failed update the catch block has already rolled back the UI and
        // app state, so bail out before the success-path overwrites that rollback.
        if (!updatedQuote) return;

        // Ensure the frequency is correctly set from the updated quote
        const frequencyInResponse = (updatedQuote?.billingInfo?.frequency ?? "monthly") as BillingFrequencyType;

        setValue("billingInfo.frequency", frequencyInResponse, { shouldTouch: true, shouldDirty: true });
        setSelectedBillingFrequency(frequencyInResponse);
        // Update the app state with the updated frequency
        updateAppState({
            billingFrequency: undefined,
            isAnnualBilling: frequencyInResponse === "yearly"
        });
    };

    return {
        selectedBillingFrequency,
        setSelectedBillingFrequency,
        frequencyToDisplay,
        showTransactionFee,
        transactionFee,
        annualSavings,
        monthlyTotal,
        annualTotal,
        annualTotalIfBilledMonthly,
        currentFrequency,
        handleFrequencyChange
    };
}
